import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	Button,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import { BoxSeriesType, QualityType } from '@/api/enum/common.enum';
import { showLabel } from '@/utils/helper';
import MeditationListSummaryFilterSection from './components/WeekTreasureSummaryFilterSection';
import SetMeditationOrderDialog from './components/SetWeekTreasureOrderDialog';
import AddTreasureDialog from './components/AddTreasureDialog';
import {
	useGetWeekTreasureListQuery,
	useResetWeekTreasureMutation,
} from '@/api/exchange/weekTreasureApiSlice';
import {
	WeeklyGameDTO,
	WeekTreasureFilter,
} from '@/api/DTO/exchange/weekTreasure.interface';
import PublishedChip from '@/common/components/PublishedChip';
import { PUBLISHED_LISTB } from '@/common/constants/common';
import ResetTvOutlinedIcon from '@mui/icons-material/ResetTvOutlined';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';

export interface DataGrid {
	id?: string;
	createdAt?: string;
}

export default function WeekTreasureListSummaryPage() {
	const showModal = useModal();
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const [resetWeekTreasure] = useResetWeekTreasureMutation();

	const qualityList = [
		{ id: QualityType.Normal, name: t('chartletList.normal') },
		{ id: QualityType.Rare, name: t('chartletList.rare') },
		{ id: QualityType.Precious, name: t('chartletList.precious') },
		{ id: QualityType.SuperPrecious, name: t('chartletList.superPrecious') },
	];

	const defaultPropertyFilter: WeekTreasureFilter = {
		pagination: initialPagination,
		searchString: '',
		selectedList: [1],
		sortings: [],
		gameTypeList: [],
		publishedList: [],
		qualityList: [],
	};
	const defaultValues: DefaultValues<WeekTreasureFilter> =
		defaultPropertyFilter;
	const methods = useForm<WeekTreasureFilter>({
		defaultValues,
	});
	const transformedSearchParams: WeekTreasureFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'gameTypeList',
			'selectedList',
			'publishedList',
			'qualityList',
		]),
	};
	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetWeekTreasureListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback((response?: WeeklyGameDTO[]) => {
		if (response == null) return;
		const newResponse: DataGrid[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				createdAt: !item.createdAt
					? '-'
					: dayjs(item.createdAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as WeekTreasureFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const handleLinkTo = (row: any) => {
		if (row.gameType === BoxSeriesType.Gallery) {
			window.open(
				`/treasury-manage/gallery-list/info/${row.gameSeriesId}/detail/${row.gameTreasureId}`,
				'_blank'
			);
		} else if (row.gameType === BoxSeriesType.Puzzle) {
			window.open(
				`/treasury-manage/puzzle-list/info/${row.gameSeriesId}/detail/${row.gameTreasureId}`,
				'_blank'
			);
		} else if (row.gameType === BoxSeriesType.Email) {
			window.open(
				`/treasury-manage/stamp-list/info/${row.gameSeriesId}/detail/${row.gameTreasureId}`,
				'_blank'
			);
		} else if (row.gameType === BoxSeriesType.Card) {
			window.open(
				`/treasury-manage/wish-list/info/${row.gameSeriesId}/detail/${row.gameTreasureId}`,
				'_blank'
			);
		} else if (row.gameType === BoxSeriesType.Chartlet) {
			window.open(
				`/treasury-manage/chartlet-list/info/${row.gameSeriesId}/detail/${row.gameTreasureId}`,
				'_blank'
			);
		}
	};

	const columns: GridColDef[] = [
		{
			field: 'gameTypeName',
			headerName: t('box.typeName'),
			flex: 10,
		},
		{
			field: 'gameSeriesName',
			headerName: t('box.seriesName'),
			flex: 10,
		},
		{
			field: 'published',
			headerName: t('box.seriesStatus'),
			flex: 10,
			renderCell: (params: any) => (
				<PublishedChip
					published={params.row.published}
					publishList={PUBLISHED_LISTB}
				/>
			),
		},
		{
			field: 'gameTreasureName',
			headerName: t('box.treasureName'),
			flex: 10,

			renderCell: (params: any) => (
				<span
					style={{ color: 'blue', cursor: 'pointer' }}
					onClick={() => handleLinkTo(params.row)}
				>
					{params.row.gameTreasureName}
				</span>
			),
		},
		{
			field: 'quality',
			headerName: t('chartletList.quality'),
			flex: 10,
			renderCell: (params: any) => showLabel(params.row.quality, qualityList),
		},
		{
			field: 'costPoints',
			headerName: t('chartletList.integral'),
			flex: 10,
		},
		{
			field: 'createdAt',
			headerName: t('weekTreasure.createTime'),
			flex: 15,
		},
	];

	const handleReset = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('weekTreasure.resetAppList')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('weekTreasure.resetAppListText')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await resetWeekTreasure(null);
					if ('data' in response) {
						enqueueSnackbar(t('weekTreasure.resetSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
					}
				}}
			/>
		));
	};

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{t('drawerListItems.weekTreasure')}
				</Typography>
				<Stack direction={'row'} alignItems={'center'} spacing={3}>
					<SetMeditationOrderDialog />
					<Button
						endIcon={<ResetTvOutlinedIcon />}
						onClick={() => handleReset()}
						sx={{ marginRight: '24px' }}
						variant='contained'
					>
						{t('button.resetList')}
					</Button>
					<AddTreasureDialog />
				</Stack>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<MeditationListSummaryFilterSection></MeditationListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
